<template>
  <div class="admin-page">
    <h2 class="page-title">充值审核</h2>
    <p class="page-hint">微信、支付宝到账后自动入账，不走本页审核。银行转账单据独立成表，审核记录只追加、不能删改。</p>
    <el-tabs v-model="tab">
      <el-tab-pane label="银行转账单据" name="bank">
        <div class="admin-toolbar">
          <el-select v-model="slipFilters.status" placeholder="状态" clearable @change="fetchSlips">
            <el-option label="待审核" value="pending" />
            <el-option label="已入账" value="approved" />
            <el-option label="已拒绝" value="rejected" />
          </el-select>
          <el-input v-model="slipFilters.keyword" class="search-input" clearable placeholder="单据号 / 转账人 / 账号 / 附言" @keyup.enter="fetchSlips" />
          <el-button type="primary" @click="fetchSlips">查询</el-button>
        </div>
        <el-table :data="slips" v-loading="slipLoading" border>
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="userId" label="用户" width="80" />
          <el-table-column prop="slipNo" label="单据号" min-width="170" show-overflow-tooltip />
          <el-table-column label="金额" width="100">
            <template #default="s">¥{{ yuan(s.row.amountCents) }}</template>
          </el-table-column>
          <el-table-column label="转出方" min-width="160" show-overflow-tooltip>
            <template #default="s">{{ payerTypeLabel(s.row.payerType) }} {{ s.row.payerName }}</template>
          </el-table-column>
          <el-table-column prop="payerBank" label="付款银行" width="120" show-overflow-tooltip />
          <el-table-column prop="payeeAccount" label="转入账号" min-width="140" show-overflow-tooltip />
          <el-table-column prop="remark" label="附言" min-width="120" show-overflow-tooltip />
          <el-table-column label="转账日期" width="120">
            <template #default="s">{{ formatDay(s.row.transferAt) }}</template>
          </el-table-column>
          <el-table-column label="状态" width="90">
            <template #default="s">
              <el-tag :type="slipStatusType(s.row.status)" size="small">{{ slipStatusLabel(s.row.status) }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="审核记录" min-width="220">
            <template #default="s">
              <div v-if="s.row.audits && s.row.audits.length" class="audit-log">
                <p v-for="a in s.row.audits" :key="a.id">
                  {{ formatTime(a.createdAt) }} {{ a.auditUserName }}（{{ a.auditRole }}）{{ a.action === 'approve' ? '通过' : '拒绝' }}
                  <span v-if="a.reason">：{{ a.reason }}</span>
                </p>
              </div>
              <span v-else>—</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template #default="s">
              <template v-if="s.row.status === 'pending'">
                <el-button link type="success" @click="auditSlip(s.row, 'approve')">通过入账</el-button>
                <el-button link type="danger" @click="openRejectSlip(s.row)">拒绝</el-button>
              </template>
              <span v-else>已锁定</span>
            </template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="slipPage"
          v-model:page-size="slipPageSize"
          :total="slipTotal"
          @size-change="fetchSlips"
          @current-change="fetchSlips"
        />
      </el-tab-pane>
      <el-tab-pane label="微信 / 支付宝订单" name="online">
        <p class="page-hint">仅查询。回调成功即入账，不能在此通过或拒绝。</p>
        <div class="admin-toolbar">
          <el-select v-model="filters.status" placeholder="状态" clearable @change="fetchOrders">
            <el-option label="待支付" value="pending" />
            <el-option label="已入账" value="completed" />
            <el-option label="已拒绝" value="rejected" />
            <el-option label="失败" value="failed" />
          </el-select>
          <el-select v-model="filters.payMethod" placeholder="支付方式" clearable @change="fetchOrders">
            <el-option label="微信" :value="1" />
            <el-option label="支付宝" :value="2" />
          </el-select>
          <el-button type="primary" @click="fetchOrders">查询</el-button>
        </div>
        <el-table :data="items" v-loading="orderLoading" border>
          <el-table-column prop="id" label="ID" width="70" />
          <el-table-column prop="userId" label="用户ID" width="90" />
          <el-table-column prop="orderNo" label="订单号" min-width="180" show-overflow-tooltip />
          <el-table-column label="金额（元）" width="110">
            <template #default="s">{{ yuan(s.row.amount) }}</template>
          </el-table-column>
          <el-table-column label="方式" width="90">
            <template #default="s">{{ payLabel(s.row.payMethod) }}</template>
          </el-table-column>
          <el-table-column prop="status" label="状态" width="90">
            <template #default="s">{{ statusLabel(s.row.status) }}</template>
          </el-table-column>
          <el-table-column prop="transactionId" label="流水号" min-width="140" show-overflow-tooltip />
          <el-table-column label="创建时间" width="170">
            <template #default="s">{{ formatTime(s.row.createdAt) }}</template>
          </el-table-column>
        </el-table>
        <AdminPagination
          v-model:current-page="page"
          v-model:page-size="pageSize"
          :total="total"
          @size-change="fetchOrders"
          @current-change="fetchOrders"
        />
      </el-tab-pane>
    </el-tabs>

    <el-dialog v-model="rejectVisible" title="拒绝银行转账单据" width="420px">
      <el-input v-model="rejectReason" type="textarea" :rows="3" placeholder="拒绝原因（用户会收到站内信）" />
      <template #footer>
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="processing" @click="confirmRejectSlip">确认拒绝</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import AdminPagination from '@/components/AdminPagination.vue'

export default {
  name: 'AdminRechargeManagement',
  components: { AdminPagination },
  data() {
    return {
      tab: 'bank',
      slipLoading: false,
      orderLoading: false,
      processing: false,
      slips: [],
      slipTotal: 0,
      slipPage: 1,
      slipPageSize: 20,
      slipFilters: { status: 'pending', keyword: '' },
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      filters: { status: '', payMethod: '' },
      rejectVisible: false,
      rejectReason: '',
      currentSlip: null
    }
  },
  created() {
    this.fetchSlips()
  },
  watch: {
    tab(val) {
      if (val === 'online' && this.items.length === 0) this.fetchOrders()
    }
  },
  methods: {
    authHeaders() {
      const token = localStorage.getItem('jwt_token')
      return token ? { Authorization: `Bearer ${token}` } : {}
    },
    yuan(v) {
      return (Number(v || 0) / 100).toFixed(2)
    },
    payLabel(m) {
      if (Number(m) === 1) return '微信'
      if (Number(m) === 2) return '支付宝'
      return '其他'
    },
    statusLabel(s) {
      return ({ pending: '待支付', completed: '已入账', rejected: '已拒绝', failed: '失败' })[s] || s
    },
    slipStatusLabel(s) {
      return ({ pending: '待审核', approved: '已入账', rejected: '已拒绝' })[s] || s
    },
    slipStatusType(s) {
      return ({ pending: 'warning', approved: 'success', rejected: 'danger' })[s] || 'info'
    },
    payerTypeLabel(t) {
      return t === 'company' ? '公司' : '个人'
    },
    formatTime(ts) {
      if (!ts) return '—'
      const n = Number(ts)
      if (!Number.isFinite(n) || n <= 0) return '—'
      return new Date(n * 1000).toLocaleString()
    },
    formatDay(ts) {
      if (!ts) return '—'
      const n = Number(ts)
      if (!Number.isFinite(n) || n <= 0) return '—'
      const d = new Date(n * 1000)
      if (Number.isNaN(d.getTime())) return '—'
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    async fetchSlips() {
      this.slipLoading = true
      try {
        const res = await axios.post('/api/v1/admin/recharge/bank/list', {
          page: this.slipPage,
          pageSize: this.slipPageSize,
          status: this.slipFilters.status || undefined,
          keyword: this.slipFilters.keyword || undefined
        }, { headers: this.authHeaders() })
        if (res.data?.code !== 200) {
          this.$message.error(res.data?.message || '加载失败')
          return
        }
        this.slips = res.data.data?.items || []
        this.slipTotal = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.slipLoading = false
      }
    },
    async fetchOrders() {
      this.orderLoading = true
      try {
        const res = await axios.post('/api/v1/admin/recharge/list', {
          page: this.page,
          pageSize: this.pageSize,
          status: this.filters.status || undefined,
          payMethod: Number(this.filters.payMethod) || 0
        }, { headers: this.authHeaders() })
        if (res.data?.code !== 200) {
          this.$message.error(res.data?.message || '加载失败')
          return
        }
        this.items = res.data.data?.orders || []
        this.total = res.data.data?.total || 0
      } catch (e) {
        this.$message.error(e.message || '加载失败')
      } finally {
        this.orderLoading = false
      }
    },
    async auditSlip(row, action, reason = '') {
      this.processing = true
      try {
        const res = await axios.post('/api/v1/admin/recharge/bank/audit', {
          id: row.id,
          action,
          reason
        }, { headers: this.authHeaders() })
        if (res.data?.code !== 200) {
          this.$message.error(res.data?.message || '处理失败')
          return
        }
        this.$message.success('处理成功')
        this.fetchSlips()
      } catch (e) {
        this.$message.error(e.message || '处理失败')
      } finally {
        this.processing = false
      }
    },
    openRejectSlip(row) {
      this.currentSlip = row
      this.rejectReason = ''
      this.rejectVisible = true
    },
    confirmRejectSlip() {
      if (!this.rejectReason.trim()) {
        this.$message.warning('请填写拒绝原因')
        return
      }
      this.auditSlip(this.currentSlip, 'reject', this.rejectReason.trim())
      this.rejectVisible = false
    }
  }
}
</script>

<style scoped>
.page-title { margin: 0 0 8px; }
.page-hint { margin: 0 0 16px; color: #5c6370; font-size: 13px; }
.admin-toolbar { display: flex; flex-wrap: wrap; gap: 12px; margin-bottom: 16px; }
.search-input { width: 280px; }
.audit-log p { margin: 0 0 4px; font-size: 12px; line-height: 1.4; color: #606266; }
</style>
